import { Camera, CameraResultType, CameraSource } from "@capacitor/camera"
import { Capacitor } from "@capacitor/core"

/**
 * Pick multiple images from the gallery (native) or file picker (web).
 * Returns an array of Files ready for upload.
 */
export async function pickImages(quality = 85): Promise<File[]> {
  if (Capacitor.isNativePlatform()) {
    try {
      const gallery = await Camera.pickImages({ quality, limit: 0 })
      const files: File[] = []
      for (const photo of gallery.photos) {
        const res = await fetch(photo.webPath)
        const blob = await res.blob()
        const ext = photo.format ?? "jpeg"
        files.push(new File([blob], `photo.${ext}`, { type: `image/${ext}` }))
      }
      return files
    } catch {
      return []
    }
  }
  // Web: trigger a multi-select file input
  return new Promise((resolve) => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.multiple = true
    input.onchange = () => resolve(Array.from(input.files ?? []))
    input.oncancel = () => resolve([])
    input.click()
  })
}

/**
 * Pick an image from camera or gallery.
 * On native: uses Capacitor Camera plugin (shows a "Camera / Gallery" sheet).
 * On web: falls back to a hidden file input.
 * Returns a File ready for upload, or null if cancelled.
 */
export async function pickImage(quality = 85): Promise<File | null> {
  if (Capacitor.isNativePlatform()) {
    try {
      const photo = await Camera.getPhoto({
        quality,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
      })
      if (!photo.dataUrl) return null
      const res = await fetch(photo.dataUrl)
      const blob = await res.blob()
      const ext = photo.format ?? "jpeg"
      return new File([blob], `photo.${ext}`, { type: `image/${ext}` })
    } catch {
      // User cancelled or permission denied
      return null
    }
  }

  // Web fallback: trigger a hidden <input type="file">
  return new Promise((resolve) => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.onchange = () => {
      const file = input.files?.[0] ?? null
      resolve(file)
    }
    input.oncancel = () => resolve(null)
    input.click()
  })
}
